import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  useContentType,
  useUpdateContentType,
  useAddField,
  useUpdateField,
  useDeleteField,
} from '@wordpuppi/shared';
import type { FieldType, ContentField } from '@wordpuppi/shared';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/LoadingSkeleton';
import { confirmDialog } from '@/lib/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TypeIcon } from '@/components/TypeIcon';
import { ArrowLeft, Plus, Pencil, Trash2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const Route = createFileRoute('/sites/$siteSlug/content-types/$typeSlug')({
  component: EditContentTypePage,
});

const FIELD_TYPES: { value: FieldType; label: string; description: string }[] = [
  { value: 'text', label: 'Text', description: 'Single line text' },
  { value: 'long_text', label: 'Long Text', description: 'Multi-line plain text' },
  { value: 'rich_text', label: 'Rich Text', description: 'Formatted content with HTML' },
  { value: 'number', label: 'Number', description: 'Integer or decimal value' },
  { value: 'boolean', label: 'Boolean', description: 'True/false toggle' },
  { value: 'date', label: 'Date', description: 'Date without time' },
  { value: 'date_time', label: 'DateTime', description: 'Date with time' },
  { value: 'enum', label: 'Enum', description: 'Single choice from a list' },
  { value: 'json', label: 'JSON', description: 'Structured JSON data' },
  { value: 'image', label: 'Image', description: 'Image file reference' },
  { value: 'file', label: 'File', description: 'File attachment' },
  { value: 'relation', label: 'Relation', description: 'Link to another content type' },
  { value: 'url', label: 'URL', description: 'Web link' },
  { value: 'email', label: 'Email', description: 'Email address' },
  { value: 'slug', label: 'Slug', description: 'URL-safe identifier' },
];

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function EditContentTypePage() {
  const { siteSlug, typeSlug } = Route.useParams();
  const { data, isLoading, error } = useContentType(siteSlug, typeSlug);
  const updateContentType = useUpdateContentType(siteSlug);
  const addField = useAddField(siteSlug, typeSlug);
  const updateField = useUpdateField(siteSlug, typeSlug);
  const deleteField = useDeleteField(siteSlug, typeSlug);

  // Edit state
  const [editName, setEditName] = useState('');
  const [editIcon, setEditIcon] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Add field state
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState<FieldType>('text');
  const [newFieldRequired, setNewFieldRequired] = useState(false);

  // Edit field state
  const [editingField, setEditingField] = useState<ContentField | null>(null);
  const [editFieldName, setEditFieldName] = useState('');
  const [editFieldRequired, setEditFieldRequired] = useState(false);

  const startEditing = () => {
    if (data?.data) {
      setEditName(data.data.name);
      setEditIcon(data.data.icon || '');
      setIsEditing(true);
    }
  };

  const handleUpdateType = async () => {
    if (!editName.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      await updateContentType.mutateAsync({
        typeSlug,
        data: {
          name: editName.trim(),
          icon: editIcon || undefined,
        },
      });
      toast.success('Content type updated');
      setIsEditing(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update');
    }
  };

  const handleAddField = async () => {
    if (!newFieldName.trim()) {
      toast.error('Field name is required');
      return;
    }

    const fieldSlug = slugify(newFieldName);
    if (data?.data?.fields.some((f) => f.slug === fieldSlug)) {
      toast.error('A field with this name already exists');
      return;
    }

    try {
      await addField.mutateAsync({
        name: newFieldName.trim(),
        slug: fieldSlug,
        field_type: newFieldType,
        required: newFieldRequired,
      });
      toast.success('Field added');
      setShowAddField(false);
      setNewFieldName('');
      setNewFieldType('text');
      setNewFieldRequired(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add field');
    }
  };

  const startEditingField = (field: ContentField) => {
    setEditingField(field);
    setEditFieldName(field.name);
    setEditFieldRequired(field.required);
  };

  const handleUpdateField = async () => {
    if (!editingField || !editFieldName.trim()) {
      toast.error('Field name is required');
      return;
    }

    try {
      await updateField.mutateAsync({
        fieldSlug: editingField.slug,
        data: {
          name: editFieldName.trim(),
          required: editFieldRequired,
        },
      });
      toast.success('Field updated');
      setEditingField(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update field');
    }
  };

  const handleDeleteField = async (fieldSlug: string, fieldName: string) => {
    if (await confirmDialog(`Delete field "${fieldName}"? This will remove data from all existing items.`, { destructive: true, confirmLabel: 'Delete field' })) {
      try {
        await deleteField.mutateAsync(fieldSlug);
        toast.success('Field deleted');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to delete field');
      }
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Handle both error states and missing data
  const contentType = data?.data;
  const hasError = error || !contentType || 'error' in contentType;

  if (hasError) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">Content type not found</h2>
        <p className="text-muted-foreground mb-4">The content type "{typeSlug}" doesn't exist.</p>
        <Button asChild>
          <Link to="/sites/$siteSlug/content-types" params={{ siteSlug }}>
            Back to Content Types
          </Link>
        </Button>
      </div>
    );
  }

  const fields = contentType.fields;

  return (
    <div>
      <div className="mb-6">
        <Link
          to="/sites/$siteSlug/content-types"
          params={{ siteSlug }}
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" />
          Back to Content Types
        </Link>
        <h1 className="text-2xl font-semibold mt-2 flex items-center gap-2">
          <TypeIcon icon={contentType.icon} className="size-6 text-muted-foreground" />
          {contentType.name}
          {contentType.is_system && (
            <Badge variant="secondary" className="ml-2">System</Badge>
          )}
        </h1>
      </div>

      {/* Type Info */}
      <Card className="mb-8">
        <CardContent className="pt-6">
          {contentType.is_system && (
            <div className="bg-muted p-3 rounded-md mb-4 text-sm text-muted-foreground">
              This is a system content type. You can add custom fields but cannot modify its core settings.
            </div>
          )}
          {isEditing ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Icon</Label>
                <Input
                  type="text"
                  value={editIcon}
                  onChange={(e) => setEditIcon(e.target.value)}
                  placeholder="e.g. users"
                  className="w-40"
                />
                <p className="text-sm text-muted-foreground">
                  A lucide icon name (like <code className="text-xs">message-circle</code>) or an emoji.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={handleUpdateType}
                  disabled={updateContentType.isPending}
                >
                  {updateContentType.isPending ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="ghost" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <div>
                  <span className="font-medium">Slug:</span>{' '}
                  <code className="bg-muted px-1.5 py-0.5 rounded text-sm">{contentType.slug}</code>
                </div>
                <div>
                  <span className="font-medium">Items:</span> {contentType.item_count}
                </div>
                <div>
                  <span className="font-medium">Created:</span>{' '}
                  {new Date(contentType.created_at).toLocaleDateString()}
                </div>
              </div>
              {!contentType.is_system && (
                <Button variant="secondary" onClick={startEditing}>
                  Edit
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fields Section */}
      <h2 className="text-xl font-semibold mb-2">Fields ({fields.length})</h2>
      <p className="text-muted-foreground text-sm mb-4">
        Fields define the structure of content items.
      </p>

      {fields.length > 0 && (
        <div className="rounded-lg border mb-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Required</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.map((field) => (
                <TableRow key={field.id}>
                  {editingField?.id === field.id ? (
                    <>
                      <TableCell>
                        <Input
                          type="text"
                          value={editFieldName}
                          onChange={(e) => setEditFieldName(e.target.value)}
                          className="w-36"
                        />
                      </TableCell>
                      <TableCell className="font-mono text-muted-foreground">
                        {field.slug}
                      </TableCell>
                      <TableCell>{field.field_type}</TableCell>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={editFieldRequired}
                          onChange={(e) => setEditFieldRequired(e.target.checked)}
                          className="rounded border-input"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={handleUpdateField}
                            disabled={updateField.isPending}
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingField(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>
                        {field.name}
                        {field.is_system && (
                          <Badge variant="secondary" className="ml-2 text-xs">
                            Core
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-muted-foreground">
                        {field.slug}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{field.field_type}</Badge>
                      </TableCell>
                      <TableCell>{field.required ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-muted-foreground hover:text-foreground"
                            title="Edit field"
                            onClick={() => startEditingField(field)}
                          >
                            <Pencil />
                            <span className="sr-only">Edit</span>
                          </Button>
                          {!field.is_system && (
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              className="text-muted-foreground hover:text-destructive"
                              title="Delete field"
                              onClick={() => handleDeleteField(field.slug, field.name)}
                              disabled={deleteField.isPending}
                            >
                              <Trash2 />
                              <span className="sr-only">Delete</span>
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add Field */}
      {showAddField ? (
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Add Field</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Field Name</Label>
              <Input
                type="text"
                value={newFieldName}
                onChange={(e) => setNewFieldName(e.target.value)}
                placeholder="e.g., Description"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Field Type</Label>
              <Select value={newFieldType} onValueChange={(v: string) => setNewFieldType(v as FieldType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((ft) => (
                    <SelectItem key={ft.value} value={ft.value}>
                      {ft.label} - {ft.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="newFieldRequired"
                checked={newFieldRequired}
                onChange={(e) => setNewFieldRequired(e.target.checked)}
                className="rounded border-input"
              />
              <Label htmlFor="newFieldRequired" className="font-normal">
                Required
              </Label>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleAddField}
                disabled={addField.isPending}
              >
                {addField.isPending ? 'Adding...' : 'Add Field'}
              </Button>
              <Button variant="ghost" onClick={() => setShowAddField(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button onClick={() => setShowAddField(true)}>
          <Plus />
          Add Field
        </Button>
      )}
    </div>
  );
}
